import React, { useRef, useState, useCallback, useEffect } from 'react'
import { Cropper } from 'react-advanced-cropper'
import 'react-advanced-cropper/dist/style.css'
import { Button, Tooltip, Slider } from '@material-ui/core'
import Loaders from '../../../custom/components/molecules/Loaders'
import { useTranslation } from 'react-i18next'

export default function ImageCropper(props) {
    const { t } = useTranslation()
    const cropperRef = useRef(null)
    const prevZoomRef = useRef(1)
    const containerRef = useRef(null)
    const footerRef = useRef(null)

    const [containerH, setContainerH] = useState(0)
    const [footerH, setFooterH] = useState(90)
    const [rotation, setRotation] = useState(0)
    const [zoom, setZoom] = useState(1)
    const [isWorking, setIsWorking] = useState(false)
    const [imgKey, setImgKey] = useState(() =>
        props.imgSrc ? `${props.imgSrc}-${Math.random().toString(36).slice(2, 8)}` : null,
    )

    // 🚫 Prevent ESC from closing popup
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation()
                e.preventDefault()
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [])

    // 📏 Measure container and footer
    useEffect(() => {
        const measure = () => {
            if (containerRef.current) setContainerH(containerRef.current.offsetHeight)
            if (footerRef.current) setFooterH(footerRef.current.offsetHeight)
        }
        measure()
        const ro = new ResizeObserver(measure)
        if (containerRef.current) ro.observe(containerRef.current)
        if (footerRef.current) ro.observe(footerRef.current)
        window.addEventListener('resize', measure)
        return () => {
            ro.disconnect()
            window.removeEventListener('resize', measure)
        }
    }, [])

    // ♻️ Reset state when new image loads
    useEffect(() => {
        setRotation(0)
        setZoom(1)
        prevZoomRef.current = 1
        setImgKey(props.imgSrc ? `${props.imgSrc}-${Math.random().toString(36).slice(2, 8)}` : null)
    }, [props.imgSrc])

    // 🧩 Set default crop to full visible area
    const setFullCropArea = useCallback(() => {
        const cropper = cropperRef.current
        if (!cropper) return
        setTimeout(() => {
            const visible = cropper.getVisibleArea?.()
            const img = cropper.getImage?.()
            if (visible && visible.width && visible.height) {
                cropper.setCoordinates({
                    left: visible.left ?? 0,
                    top: visible.top ?? 0,
                    width: visible.width,
                    height: visible.height,
                })
            } else if (img && img.width && img.height) {
                cropper.setCoordinates({
                    left: 0,
                    top: 0,
                    width: img.width,
                    height: img.height,
                })
            }
        }, 80)
    }, [])

    const handleCropperReady = useCallback(() => setFullCropArea(), [setFullCropArea])

    // 🔄 Rotate & Flip
    const handleRotateCW = useCallback(() => {
        cropperRef.current?.rotateImage(90)
        setRotation((p) => (p + 90) % 360)
        setFullCropArea()
    }, [setFullCropArea])

    const handleRotateCCW = useCallback(() => {
        cropperRef.current?.rotateImage(-90)
        setRotation((p) => (p - 90 + 360) % 360)
        setFullCropArea()
    }, [setFullCropArea])

    const handleFlipHorizontal = useCallback(() => {
        cropperRef.current?.flipImage(true, false)
        setFullCropArea()
    }, [setFullCropArea])

    const handleFlipVertical = useCallback(() => {
        cropperRef.current?.flipImage(false, true)
        setFullCropArea()
    }, [setFullCropArea])

    const handleZoomChange = useCallback((_, val) => {
        const cropper = cropperRef.current
        if (!cropper) return
        const zoomVal = Array.isArray(val) ? val[0] : val
        const prev = prevZoomRef.current || 1
        const delta = zoomVal / prev
        cropper.zoomImage(delta)
        prevZoomRef.current = zoomVal
        setZoom(zoomVal)
    }, [])

    // 💾 Save cropped image
    const onCropSave = useCallback(() => {
        if (!props.imgSrc) return
        try {
            setIsWorking(true)
            const canvas = cropperRef.current?.getCanvas()
            if (!canvas) {
                setIsWorking(false)
                return
            }
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        props.getCroppedImage?.(blob)
                        props.onClose?.()
                    }
                    setIsWorking(false)
                },
                'image/jpeg',
                0.92,
            )
        } catch (err) {
            console.error('❌ onCropSave failed:', err)
            setIsWorking(false)
        }
    }, [props.imgSrc, props.getCroppedImage, props.onClose])

    const loading = props.loading || !props.imgSrc

    // 🧠 Smart crop height
    const isMobile = window.innerWidth < 768
    const maxAvailable = containerH - footerH - 20
    const cropHeight = Math.max(300, isMobile ? maxAvailable * 0.7 : maxAvailable * 0.8)

    return (
        <>
            {loading ? (
                <div className="noGrid cropLoader">
                    <Loaders isLoading={true} />
                </div>
            ) : (
                <div
                    ref={containerRef}
                    className="imageCropCnt"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        width: '100%',
                        height: '100%',
                        maxHeight: '90vh',
                        background: 'transparent', // ✅ transparent base
                        overflow: 'hidden',
                        position: 'relative',
                        borderRadius: '8px',
                    }}
                >
                    {/* 🖼️ Crop Zone */}
                    <div
                        style={{
                            width: '100%',
                            height: `${cropHeight}px`,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            background: 'transparent', // ✅ fully transparent crop area
                            overflow: 'hidden',
                            flexShrink: 0,
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                        }}
                    >
                        <Cropper
                            key={imgKey}
                            ref={cropperRef}
                            src={props.imgSrc}
                            onReady={handleCropperReady}
                            stencilProps={{
                                movable: true,
                                resizable: true,
                            }}
                            imageRestriction="stencil"
                            className="advanced-cropper"
                            style={{
                                width: '100%',
                                height: '100%',
                                background: 'transparent', // ✅ transparent behind image
                            }}
                        />
                        {isWorking && (
                            <div
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'grid',
                                    placeItems: 'center',
                                    background: 'rgba(0,0,0,0.35)',
                                }}
                            >
                                <Loaders isLoading={true} />
                            </div>
                        )}
                    </div>

                    {/* 🎛️ Toolbar */}
                    <div
                        ref={footerRef}
                        className="crop-controls"
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 16,
                            padding: '14px 16px',
                            background: 'rgba(0, 0, 0, 0.6)',
                            backdropFilter: 'blur(8px)',
                            borderTopLeftRadius: 10,
                            borderTopRightRadius: 10,
                            boxShadow: '0 -2px 8px rgba(0,0,0,0.25)',
                            flexShrink: 0,
                        }}
                    >
                        <Tooltip title="Rotate Left">
                            <Button onClick={handleRotateCCW} disabled={isWorking}>
                                <span className="material-icons" style={{ color: '#fff' }}>
                                    rotate_90_degrees_ccw
                                </span>
                            </Button>
                        </Tooltip>

                        <Tooltip title="Rotate Right">
                            <Button onClick={handleRotateCW} disabled={isWorking}>
                                <span className="material-icons" style={{ color: '#fff' }}>
                                    rotate_90_degrees_cw
                                </span>
                            </Button>
                        </Tooltip>

                        <Tooltip title="Flip Left–Right">
                            <Button onClick={handleFlipHorizontal} disabled={isWorking}>
                                <span className="material-icons" style={{ color: '#fff' }}>
                                    flip
                                </span>
                            </Button>
                        </Tooltip>

                        <Tooltip title="Flip Up–Down">
                            <Button onClick={handleFlipVertical} disabled={isWorking}>
                                <span className="material-icons" style={{ color: '#fff' }}>
                                    flip_camera_android
                                </span>
                            </Button>
                        </Tooltip>

                        {/* Zoom */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                minWidth: 160,
                                flexGrow: 1,
                                maxWidth: 250,
                            }}
                        >
                            <span className="material-icons" style={{ color: '#fff' }}>
                                zoom_out
                            </span>
                            <Slider
                                value={zoom}
                                min={1}
                                max={5}
                                step={0.01}
                                onChange={handleZoomChange}
                                disabled={isWorking}
                                style={{ color: '#fff', marginTop: 8 }}
                            />
                            <span className="material-icons" style={{ color: '#fff' }}>
                                zoom_in
                            </span>
                        </div>

                        {/* ❤️ Save Button */}
                        <Button
                            className="saveBtn"
                            variant="contained"
                            onClick={onCropSave}
                            disabled={isWorking}
                            style={{
                                minWidth: 160,
                                fontWeight: 600,
                                borderRadius: 8,
                                background: 'var(--primaryColor)',
                                color: '#fff',
                                padding: '10px 20px',
                                textTransform: 'none',
                                boxShadow: '0 2px 8px rgba(119, 41, 54, 0.4)',
                            }}
                            onMouseOver={(e) =>
                                (e.currentTarget.style.background = 'rgba(119, 41, 54, 0.9)')
                            }
                            onMouseOut={(e) =>
                                (e.currentTarget.style.background = 'var(--primaryColor)')
                            }
                        >
                            {isWorking ? t('Saving...') : t('Save Selection')}
                        </Button>
                    </div>
                </div>
            )}
        </>
    )
}
